import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IAPIListResult, IPagination, IPaginationDate, IPaginationResult, IPaginationResultPagesList } from "app/common/interfaces";
import { AbstractService } from "app/services/abstract.service";
import { environment as env } from "environments/environment.prod";
import { catchError, map, Observable } from "rxjs";
import { OrderAttributes, TripAttributes } from "@albatrosdeveloper/ave-models-package";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { ClientMaterialSummary, ClientOrder, groupMaterialsByClient, GroupedMaterialApiResponse, GroupedMaterialWithCustomers } from "../monitor-routes/interfaces/material-group-partner";
import { BatchInfo, DocumentItem, WorkProgressMaterialItem, WorkProgressMaterialsResponse } from "../monitor-routes/interfaces/work-progress-material.interface";
import { DOCUMENT_TYPES_WORK_IN_PROGRESS_TRIPS_SECTION, DocumentTypeEnum } from "app/common/enums/document-types.enum";
import { IFilters } from "../../filters/interfaces/filterInterface";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { OrderTripDate } from "./interfaces/trip.interface";
import { GroupData } from "app/shared/components/map-modal/models/map-modal.interface";

const apiUrl = env.API_AVE_TRIP_URL;
const apiOrder = env.API_AVE_ORDER_URL;
const moduleName = "trip";
const order = "order";

@Injectable({
  providedIn: "root",
})
export class PrincipalScreenService extends AbstractService<
  TripAttributes,
  Partial<TripAttributes>,
  Partial<TripAttributes>
> {
  currentFilter: CombinedFilter<TripAttributes>;
  currentFilterData: any;
  filterToExportLD: CombinedFilter<OrderAttributes>;
  filterToExportES: CombinedFilter<OrderAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCountByTrip(
    options: IPaginationDate<TripAttributes>,
    date,
    routeIds?: number[], // Add your companyIds if necessary
    companyIds?: string,
    salesOfficeId?: number[],
    type?: string[],
    tripId?: number,
  ): Observable<IPaginationResult<TripAttributes>> {
    const query = buildQuery<TripAttributes>(
      where(
        "subRoute.name" as keyof TripAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "user.user_login.username" as keyof TripAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      andAllWhere("dateHourStartTeoric", Ops.btwnDates(date.start, date.end)),
      routeIds && routeIds.length > 0
        ? andAllWhere(
          "subRoute.id" as keyof TripAttributes,
          Ops.in(...routeIds)
        )
        : noop(),
      salesOfficeId && salesOfficeId.length > 0
        ? andAllWhere(
          "salesOffice.id" as keyof TripAttributes,
          Ops.in(...salesOfficeId)
        )
        : noop(),
      type && type.length > 0
        ? andAllWhere("subRoute.type" as keyof TripAttributes, Ops.in(...type))
        : noop(),
      companyIds && companyIds.length > 0
        ? andAllWhere("company.id" as keyof TripAttributes, Ops.in(companyIds))
        : noop(),
      tripId
        ? andAllWhere("id", Ops.eq(tripId))
        : noop(),
      orderBy(
        options.sort ?? ("dateHourStartTeoric" as keyof TripAttributes),
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;
    //this.currentFilterData = dataToFilter;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCountByTrip?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<TripAttributes>>(API_URL);
  }

  /**
 * Obtiene todos los viajes para vista árbol (sin paginación)
 * @param dataToFilter Filtros aplicados
 * @returns Todos los registros para construir el árbol
 */
  findAllForTreeView(
    dataToFilter: IFilters
  ): Observable<IPaginationResult<TripAttributes>> {
    const query = buildQuery<TripAttributes>(
      where(
        "subRoute.name" as keyof TripAttributes,
        Ops.like("", "i")
      ),
      dataToFilter && dataToFilter.startTrip && dataToFilter.endTrip
        ? andAllWhere("dateHourStartTeoric", Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip))
        : noop(),

      dataToFilter && dataToFilter.end_startTrip && dataToFilter.end_endTrip
        ? andAllWhere("dateHourEndTeoric", Ops.btwnDates(dataToFilter.end_startTrip, dataToFilter.end_endTrip))
        : noop(),

      dataToFilter && dataToFilter.charge_start && dataToFilter.charge_end
        ? andAllWhere("loadDateTime", Ops.btwnDates(dataToFilter.charge_start, dataToFilter.charge_end))
        : noop(),

      dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
        ? andAllWhere("subRoute.id" as keyof TripAttributes, Ops.in(...dataToFilter.subRouteId))
        : noop(),

      dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0
        ? andAllWhere("salesOffice.id" as keyof TripAttributes, Ops.in(...dataToFilter.salesOfficeId))
        : noop(),

      dataToFilter.routeTypeId
        ? andAllWhere("subRoute.type" as keyof TripAttributes, Ops.eq(dataToFilter.routeTypeId))
        : noop(),

      dataToFilter.companyId
        ? andAllWhere("company.id" as keyof TripAttributes, Ops.eq(dataToFilter.companyId))
        : noop(),

      dataToFilter.ordersToFilter
        ? andAllWhere("id", Ops.in(...dataToFilter.ordersToFilter.map(id => Number(id))))
        : noop(),

      dataToFilter.functionCodes && dataToFilter.functionCodes.length > 0
        ? andAllWhere("subRoute.function.code" as keyof TripAttributes, Ops.in(...dataToFilter.functionCodes))
        : noop(),

      dataToFilter.functionNames && dataToFilter.functionNames.length > 0
        ? andAllWhere("subRoute.function.name" as keyof TripAttributes, Ops.in(...dataToFilter.functionNames))
        : noop(),

      dataToFilter.tripStatuses && dataToFilter.tripStatuses.length > 0
        ? andAllWhere("statusTripId" as keyof TripAttributes, Ops.in(...dataToFilter.tripStatuses))
        : noop(),

      dataToFilter.multiVehicle != null
        ? andAllWhere("multiVehicle" as keyof TripAttributes, Ops.eq(dataToFilter.multiVehicle))
        : noop(),
      dataToFilter.multiDevice != null
        ? andAllWhere("multiDevice" as keyof TripAttributes, Ops.eq(dataToFilter.multiDevice))
        : noop(),
      dataToFilter.hasUnifiedLoadDocs != null
        ? andAllWhere("hasUnifiedLoadDocs" as keyof TripAttributes, Ops.eq(dataToFilter.hasUnifiedLoadDocs))
        : noop(),

      orderBy(
        "statusTripId" as keyof TripAttributes,
        OrderVariant.Asc
      ),
      noop() // ← SIN PAGINACIÓN para modo árbol
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCountByTripv2`;
    return this.http.post<IPaginationResult<TripAttributes>>(API_URL, {
      filter: query
    });
  }

  /**
   * Obtiene viajes con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Cuatro objetos (OR lógico) - busca en 'subRoute.name', 'user.user_login.username', 'subRoute.externalKeys.code', 'id'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply (dates, subRouteId, salesOfficeId, etc.)
   * @param isLiquidationSerial Filter by liquidation serial
   * @returns Paginated data
   *
   * 📍 Used in:
   * - Reportes/Ventas/Liquidaciones (`liquidations.component.ts`)
   * - Reportes/Ventas/Liquidaciones (export)
   * - Ventas/Monitor/De rutas (`principal-screen.component.ts`)
   *
   * Search fields: subRoute.name, user.user_login.username, subRoute.externalKeys.code, id
   * Array filters: subRouteId[], salesOfficeId[], ordersToFilter[], functionCodes[], masterUserId[], tripStatuses[], functionNames[]
   * Single filters: routeTypeId, companyId
   * Date filters: startTrip/endTrip, end_startTrip/end_endTrip, charge_start/charge_end
   * Conditional filters: isLiquidationSerial
   */
  findAllWithCount(
    options: IPaginationDate<TripAttributes>,
    dataToFilter: IFilters,
    isLiquidationSerial?: boolean
  ): Observable<IPaginationResult<TripAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: filtros de fechas, rutas, oficinas, funciones, usuarios, estados, liquidación.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<TripAttributes>[] => {
      // Si es el primer objeto, no hay filtro inicial porque no hay _deleted en TripAttributes
      // Solo aplicamos los filtros condicionales
      const filterFn = params?.useOrWhere ? orWhere : andWhere;

      return [
        filterFn("_deleted", Ops.eq(false)),
        // Filtros de fechas
        dataToFilter && dataToFilter.startTrip && dataToFilter.endTrip
          ? andWhere("dateHourStartTeoric", Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip))
          : noop(),
        dataToFilter && dataToFilter.end_startTrip && dataToFilter.end_endTrip
          ? andWhere("dateHourEndTeoric", Ops.btwnDates(dataToFilter.end_startTrip, dataToFilter.end_endTrip))
          : noop(),
        dataToFilter && dataToFilter.charge_start && dataToFilter.charge_end
          ? andWhere("loadDateTime", Ops.btwnDates(dataToFilter.charge_start, dataToFilter.charge_end))
          : noop(),
        // Filtros de arrays
        dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
          ? andWhere("subRoute.id" as keyof TripAttributes, Ops.in(...dataToFilter.subRouteId))
          : noop(),
        dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0
          ? andWhere("salesOffice.id" as keyof TripAttributes, Ops.in(...dataToFilter.salesOfficeId))
          : noop(),
        dataToFilter.ordersToFilter && dataToFilter.ordersToFilter.length > 0
          ? andWhere("id", Ops.in(...dataToFilter.ordersToFilter.map(id => Number(id))))
          : noop(),
        dataToFilter.functionCodes && dataToFilter.functionCodes.length > 0
          ? andWhere("subRoute.function.code" as keyof TripAttributes, Ops.in(...dataToFilter.functionCodes))
          : noop(),
        dataToFilter.masterUserId && dataToFilter.masterUserId.length > 0
          ? andWhere("user.user_login.username" as keyof TripAttributes, Ops.in(...dataToFilter.masterUserId))
          : noop(),
        dataToFilter.tripStatuses && dataToFilter.tripStatuses.length > 0
          ? andWhere("statusTripId" as keyof TripAttributes, Ops.in(...dataToFilter.tripStatuses))
          : noop(),
        dataToFilter.functionNames && dataToFilter.functionNames.length > 0
          ? andWhere("subRoute.function.name" as keyof TripAttributes, Ops.in(...dataToFilter.functionNames))
          : noop(),
        // Filtros de valores únicos
        dataToFilter.routeTypeId && dataToFilter.routeTypeId.length > 0
          ? andWhere("subRoute.type" as keyof TripAttributes, Ops.eq(dataToFilter.routeTypeId))
          : noop(),
        dataToFilter.companyId 
          ? andWhere("company.id" as keyof TripAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        // Filtro condicional
        isLiquidationSerial
          ? andWhere("liquidationSerial", Ops.not(null))
          : noop(),
        // Filtros multi-vehículo
        dataToFilter.multiVehicle != null
          ? andWhere("multiVehicle" as keyof TripAttributes, Ops.eq(dataToFilter.multiVehicle))
          : noop(),
        dataToFilter.multiDevice != null
          ? andWhere("multiDevice" as keyof TripAttributes, Ops.eq(dataToFilter.multiDevice))
          : noop(),
        dataToFilter.hasUnifiedLoadDocs != null
          ? andWhere("hasUnifiedLoadDocs" as keyof TripAttributes, Ops.eq(dataToFilter.hasUnifiedLoadDocs))
          : noop(),
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por subRoute.name (o solo condiciones si no hay búsqueda)
    const searchBySubRouteNameQueryObject: FilterFn<TripAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch
        ? andWhere("subRoute.name" as keyof TripAttributes, Ops.like(options.search!, "i"))
        : noop(),  // Filtro neutro
    ];

    // Objeto 2: Condiciones base + búsqueda por user.user_login.username (solo si hay búsqueda)
    const searchByUsernameQueryObject: FilterFn<TripAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("user.user_login.username" as keyof TripAttributes, Ops.like(options.search!, "i")),
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por subRoute.externalKeys.code (solo si hay búsqueda)
    const searchByExternalKeysQueryObject: FilterFn<TripAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("subRoute.externalKeys.code" as keyof TripAttributes, Ops.like(options.search!, "i")),
    ] : [];

    // Objeto 4: Condiciones base + búsqueda por ID (solo si hay búsqueda y es un número válido)
    const searchNumber = Number(options.search);
    const isValidNumber = hasSearch && !isNaN(searchNumber) && searchNumber > 0;
    const searchByIdQueryObject: FilterFn<TripAttributes>[] = isValidNumber ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("id", Ops.eq(searchNumber)),

    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<TripAttributes>(
      // Expandimos los cuatro objetos en el buildQuery
      ...searchBySubRouteNameQueryObject,
      ...searchByUsernameQueryObject,
      ...searchByExternalKeysQueryObject,
      ...searchByIdQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? ("dateHourStartTeoric" as keyof TripAttributes),
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;
    //this.currentFilterData = dataToFilter;

    const API_URL: string = apiUrl + `api/${moduleName}/withCountByTripv2`;
    return this.http.post<IPaginationResult<TripAttributes>>(API_URL, {
      filter: query
    });
  }

  /**
   * Obtiene viajes por sus IDs específicos
   * @param tripIds Array de IDs de viajes a buscar
   * @param options Opciones de paginación (opcional)
   * @returns Observable con los viajes encontrados
   */
  findTripsByIds(
    tripIds: number[],
    options?: IPaginationDate<TripAttributes>
  ): Observable<IPaginationResult<TripAttributes>> {
    if (!tripIds || tripIds.length === 0) {
      // Retornar resultado vacío si no hay IDs
      return new Observable(subscriber => {
        subscriber.next({
          status: "success",
          statusCode: "200",
          data: [{
            data: [],
            pageInfo: {
              total: 0,
              page: 1,
              pageSize: 100
            }
          }]
        } as IPaginationResult<TripAttributes>);
        subscriber.complete();
      });
    }

    const query = buildQuery<TripAttributes>(
      where("id", Ops.in(...tripIds)),
      orderBy(
        options?.sort ?? ("dateHourStartTeoric" as keyof TripAttributes),
        options?.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(1, tripIds.length) // Traer todos los viajes pineados en una sola página
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCountByTripv2`;
    return this.http.post<IPaginationResult<TripAttributes>>(API_URL, {
      filter: query
    });
  }

  /**
  * Pagination
  * @param options Pagination options
  * @returns Paginated data
  */
  findAllByFilters(
    options: IPaginationDate<TripAttributes>,
    dataToFilter: IFilters,
    isLiquidationSerial?: boolean
  ): Observable<IPaginationResult<TripAttributes>> {
    const query = buildQuery<TripAttributes>(
      where(
        "subRoute.name" as keyof TripAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "user.user_login.username" as keyof TripAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "subRoute.externalKeys.code" as keyof TripAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "id",
        Ops.eq(Number(options.search ?? ""))
      ),
      dataToFilter && dataToFilter.startTrip && dataToFilter.endTrip
        ? andAllWhere("dateHourStartTeoric", Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip))
        : noop(),

      dataToFilter && dataToFilter.end_startTrip && dataToFilter.end_endTrip
        ? andAllWhere("dateHourEndTeoric", Ops.btwnDates(dataToFilter.end_startTrip, dataToFilter.end_endTrip))
        : noop(),

      dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
        ? andAllWhere(
          "subRoute.id" as keyof TripAttributes,
          Ops.in(...dataToFilter.subRouteId)
        )
        : noop(),
      dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0
        ? andAllWhere(
          "salesOffice.id" as keyof TripAttributes,
          Ops.in(...dataToFilter.salesOfficeId)
        )
        : noop(),
      dataToFilter.routeTypeId
        ? andAllWhere("subRoute.type" as keyof TripAttributes, Ops.eq(dataToFilter.routeTypeId))
        : noop(),
      dataToFilter.companyId
        ? andAllWhere("company.id" as keyof TripAttributes, Ops.eq(dataToFilter.companyId))
        : noop(),
      dataToFilter.ordersToFilter
        ? andAllWhere("id", Ops.in(...dataToFilter.ordersToFilter.map(id => Number(id))))
        : noop(),

      dataToFilter.functionCodes && dataToFilter.functionCodes.length > 0
        ? andAllWhere("subRoute.function.code" as keyof TripAttributes, Ops.in(...dataToFilter.functionCodes))
        : noop(),

      dataToFilter.masterUserId && dataToFilter.masterUserId.length > 0
        ? andAllWhere("user.user_login.username" as keyof TripAttributes, Ops.in(...dataToFilter.masterUserId))
        : noop(),

      isLiquidationSerial ? andAllWhere("liquidationSerial", Ops.not(null)) : noop(),

      orderBy(
        options.sort ?? ("dateHourStartTeoric" as keyof TripAttributes),
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;
    //this.currentFilterData = dataToFilter;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCountByTripv2?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<TripAttributes>>(API_URL);
  }

  /**
      * Pagination
      * Servicio para traer todos los documentos en monitor y agenda
      * @param options Pagination options
      * @returns Paginated data
      */
  findAllWithCountForDocument(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[],
    statusIds?: number[],
    serialT?: boolean,
    isDirectSale?: boolean
  ): Observable<IPaginationResult<OrderAttributes>> {
    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andAllWhere('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      statusIds && statusIds.length > 0
        ? andAllWhere(
          "statusId" as keyof OrderAttributes,
          Ops.in(...statusIds)
        )
        : noop(),
      serialT
        ? andAllWhere(
          "transportDocumentSerial" as keyof OrderAttributes,
          Ops.eq(null)
        )
        : noop(),
      // SOLO PARA ENTREGAS
      isDirectSale
        ? andAllWhere(
          "isDirectSale" as keyof OrderAttributes,
          Ops.eq(false)
        )
        : noop(),
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiOrder +
      `api/${order}/withCountByTrip?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCountForDocumentNotIn(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[]
  ): Observable<IPaginationResult<OrderAttributes>> {
    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere('type.code' as keyof OrderAttributes, Ops.notIn(...document_type)),
      andWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      orderBy(
        options.sort ?? "total",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiOrder +
      `api/${order}/withCountByTrip?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL);
  }

  /**
    * Pagination
    * @param options Pagination options
    * @returns Paginated data
    */
  getOrdersGroupedByCustomers(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[]
  ): Observable<IPaginationResult<OrderAttributes>> {
    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andAllWhere('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiOrder +
      `api/${order}/ship_to?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL);
  }

  /**
      * Obtiene las órdenes agrupadas por material y la informacion de lotes. 
      * Pagination
      * @param options Pagination options
      * @returns Paginated data
      */
  getOrdersGroupedByMaterial(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[],
    isDirectSale?: boolean
  ): Observable<IPaginationResult<OrderAttributes>> {
    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andAllWhere('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),

      isDirectSale
        ? andAllWhere(
          "isDirectSale" as keyof OrderAttributes,
          Ops.eq(false)
        )
        : noop(),

      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    if (document_type.includes(DocumentTypeEnum.DOCUMENTO_DE_CARGA)) {
      this.filterToExportLD = query;
    } else if (document_type.includes(DocumentTypeEnum.ENTREGA_ENTRADA) || document_type.includes(DocumentTypeEnum.ENTREGA_SALIDA)) {
      this.filterToExportES = query;
    }

    const API_URL: string =
      apiOrder +
      `api/${order}/group_material?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL);
  }

  /**
        * Pagination
        * @param options Pagination options
        * @returns Paginated data
        */
  findAllWithCountForClient(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[]
  ): Observable<IPaginationResult<OrderAttributes>> {
    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere('type.code' as keyof OrderAttributes, Ops.in(...[ 
        DocumentTypeEnum.FACTURA,
        DocumentTypeEnum.PEDIDO,
        DocumentTypeEnum.COTIZACION,
        DocumentTypeEnum.NOTA_DE_CREDITO,
        DocumentTypeEnum.NOTA_DE_DEBITO,
        DocumentTypeEnum.DOCUMENTO_DE_PAGO,
        DocumentTypeEnum.SOLICITUD_CAMBIO_PRODUCTO,
        DocumentTypeEnum.SOLICITUD_DEVOLUCION_PRODUCTO,
        DocumentTypeEnum.CAMBIO_DE_PRODUCTO,
        DocumentTypeEnum.DEVOLUCION_DE_PRODUCTO,
        DocumentTypeEnum.LIQUIDACION,
        DocumentTypeEnum.DOCUMENTO_MERMA])),
      // andAllWhere('class.hasExternalInvoice' as keyof OrderAttributes, Ops.eq(true)),
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiOrder +
      `api/${order}/ship_to?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL);
  }

  /**
     * Pagination
     * @param options Pagination options
     * @returns Paginated data
     */
  findAllWithCountForDeliveries(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[],
    statusIds?: number[],
    serial?: string[]
  ): Observable<IPaginationResult<OrderAttributes>> {
    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andAllWhere('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      statusIds && statusIds.length > 0
        ? andAllWhere(
          "statusId" as keyof OrderAttributes,
          Ops.in(...statusIds)
        )
        : noop(),
      serial && serial.length > 0
        ? andAllWhere(
          "serial" as keyof OrderAttributes,
          Ops.in(...serial)
        )
        : noop(),

      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiOrder +
      `api/${order}/withCountByTrip?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL);
  }

  findTripFuture(
    options: IPaginationDate<TripAttributes>,
    date,
    routeIds?: number[], // Add your companyIds if necessary
    companyIds?: string,
    salesOfficeId?: number[],
    type?: string[],
  ): Observable<any> {
    const query = buildQuery<TripAttributes>(
      where(
        "subRoute.name" as keyof TripAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "user.user_login.username" as keyof TripAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "subRoute.externalKeys.code" as keyof TripAttributes,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "id",
        Ops.eq(Number(options.search ?? ""))
      ),
      date && date.start && date.end
        ? andAllWhere("dateHourStartTeoric", Ops.btwnDates(date.start, date.end))
        : noop(),

      routeIds && routeIds.length > 0
        ? andAllWhere(
          "subRoute.id" as keyof TripAttributes,
          Ops.in(...routeIds)
        )
        : noop(),
      salesOfficeId && salesOfficeId.length > 0
        ? andAllWhere(
          "salesOffice.id" as keyof TripAttributes,
          Ops.in(...salesOfficeId)
        )
        : noop(),
      type && type.length > 0
        ? andAllWhere("subRoute.type" as keyof TripAttributes, Ops.in(...type))
        : noop(),
      companyIds && companyIds.length > 0
        ? andAllWhere("company.id" as keyof TripAttributes, Ops.in(companyIds))
        : noop(),
      orderBy(
        options.sort ?? ("dateHourStartTeoric" as keyof TripAttributes),
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCountByDate?filter=${encodeURIComponent(JSON.stringify(query))}`;
    const headers = new HttpHeaders({ 'x-ave-ctx': 'skip-loading' });
    return this.http.get<IPaginationResult<any>>(API_URL, { headers });
  }

  cancelTrip(data: {
    tripMongoId: string,
    method: string, //"TOTAL, PARTIAL"
  }){
    const API_URL: string = apiUrl + `api/${moduleName}/cancel_trip`;
    return this.http.post(API_URL, data);
  }

  //
  getCustomersByDocument(payload: {
    entryDeliverySerials: string
  }) {
    const API_URL: string = apiOrder + `api/${order}/get_customers_from_entry_document`;
    return this.http.post<any>(API_URL, payload);
  }

  postCustomerAddDocument(payload: any) {
    const API_URL: string = apiOrder + `api/${order}/add_new_detail_to_entry_document`;
    return this.http.post<any>(API_URL, payload);
  }



  /**
      * Pagination
      * Servicio para traer todos los documentos en monitor y agenda
      * @param options Pagination options
      * @returns Paginated data
      */
  findAllWithCountForInvoice(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[],
    statusIds?: number[],
    document?: string
  ): Observable<IPaginationResult<OrderAttributes>> {
    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andAllWhere('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      andAllWhere('serial' as keyof OrderAttributes, Ops.eq(document)),
      statusIds && statusIds.length > 0
        ? andAllWhere(
          "statusId" as keyof OrderAttributes,
          Ops.notIn(...statusIds)
        )
        : noop(),
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiOrder +
      `api/${order}/withCountByTrip?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL);
  }

  /**
   * Obtiene las órdenes agrupadas por material y la información de lotes para exportar.
   * @param filter Filtros para cargar la data de paginación
   * @returns Data de paginación
   */
  getOrdersGroupedByMaterialExport(
    filter: CombinedFilter<OrderAttributes>
  ): Observable<IPaginationResult<OrderAttributes>> {
    const API_URL: string =
      apiOrder +
      `api/${order}/group_material?filter=${encodeURIComponent(JSON.stringify(filter))}`;

    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL);
  }

  // Agregar este método al servicio existente

  /**
   * Obtiene las órdenes agrupadas por clientes con sus materiales consolidados
   * La paginación se maneja en el backend
   */
  getOrdersGroupedByCustomersWithMaterials(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[]
  ): Observable<IPaginationResultPagesList<ClientMaterialSummary>> {

    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andAllWhere('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );


    const API_URL: string =
      apiOrder +
      `api/${order}/ship_to?filter=${encodeURIComponent(JSON.stringify(query))}`;

    return this.http.get<IPaginationResultPagesList<OrderAttributes>>(API_URL).pipe(
      map((response: IPaginationResultPagesList<OrderAttributes>) => {
        // Obtener los datos de las órdenes
        const ordersData = response?.data?.[0]?.data || [];

        // Convertir a ClientOrder[] para usar la función de agrupación existente
        const clientOrders: ClientOrder[] = ordersData as any;

        // Agrupar materiales por cliente usando la función existente
        const groupedMaterials: ClientMaterialSummary[] = groupMaterialsByClient(clientOrders);

        // Construir la respuesta con el formato IPaginationResult
        const result: IPaginationResultPagesList<ClientMaterialSummary> = {
          status: response.status,
          statusCode: response.statusCode,
          data: [{
            data: groupedMaterials,
            pageInfo: response.data?.[0]?.pageInfo || []
          }]
        };

        return result;
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Obtiene materiales agrupados con los clientes debajo (estructura invertida)
   * Utiliza el nuevo endpoint /grouped_material
   */
  getGroupedMaterialsByCustomers(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[]
  ): Observable<IPaginationResultPagesList<GroupedMaterialWithCustomers>> {

    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andAllWhere('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      orderBy(
        (options.sort ?? "shipTo.name") as any,
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiOrder +
      `api/${order}/grouped_material?filter=${encodeURIComponent(JSON.stringify(query))}`;

    return this.http.get<GroupedMaterialApiResponse>(API_URL).pipe(
      map((response: GroupedMaterialApiResponse) => {
        // Transform the response to match our expected format
        const groupedMaterials = response?.data?.data || [];

        const result: IPaginationResultPagesList<GroupedMaterialWithCustomers> = {
          status: response.status || "success",
          statusCode: response.statusCode || "200",
          data: [{
            data: groupedMaterials,
            pageInfo: (response.data?.pageInfo || []).map(pageInfo => ({
              total: pageInfo.total,
              id: pageInfo.page
            }))
          }]
        };

        return result;
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  /**
   * Obtiene materiales de trabajo en curso usando el endpoint existente
   * @param tripId ID del viaje
   * @param documentTypes Tipos de documento (ej: [DocumentTypeEnum.FACTURA, DocumentTypeEnum.NOTA_DE_CREDITO])
   * @param options Opciones de paginación
   */
  async getWorkInProgressMaterials(
    tripId: number,
    documentTypes: string[],
    options: IPagination<OrderAttributes> = {
      currentPage: 1,
      limitPerPage: 100,
      search: "",
      sort: "createdAt",
      sortDirection: "ASC"
    }
  ): Promise<WorkProgressMaterialsResponse> {
    try {
      // @ts-ignore
      // Usar el método existente para obtener documentos
      const response = await this.findAllWithCountForDocument(
        options,
        tripId,
        documentTypes
      ).toPromise();


      // @ts-ignore
      const documents: DocumentItem[] = response?.data || [];
      const materialsList: WorkProgressMaterialItem[] = [];

      // Iterar sobre cada documento
      documents.forEach(document => {

        // Iterar sobre cada detalle del documento
        if (document.detail && Array.isArray(document.detail)) {
          document.detail.forEach(detailItem => {
            const material = detailItem.material;

            if (material) {
              // 🆕 Procesar información de lotes
              const batches: BatchInfo[] = [];
              if (detailItem.batches && Array.isArray(detailItem.batches) && detailItem.batches.length > 0) {
                detailItem.batches.forEach(batch => {
                  batches.push({
                    batchNumber: batch.batchNumber || batch.batch || batch.lote || '',
                    quantity: batch.quantity || batch.cantidad || 0,
                    expirationDate: batch.expirationDate || batch.fechaVencimiento || null,
                    manufacturingDate: batch.manufacturingDate || batch.fechaFabricacion || null,
                    // Información adicional que pueda existir
                    status: batch.status || batch.estado || null,
                    warehouse: batch.warehouse || batch.almacen || null,
                    labels: batch?.labels || [],
                    parallelQuantity: batch?.parallelQuantity || 0
                  });
                });
              }

              // Crear objeto de material básico
              const materialItem: WorkProgressMaterialItem = {
                // Información del material
                materialCode: material.code,
                materialName: material.name,

                // Información del cliente
                clientCode: document.shipTo?.externalKeys?.code || '',
                clientName: document.shipTo?.name || '',

                // Información del documento
                documentNumber: document.serial,
                documentClass: document.class?.name || document.type?.name || '',

                // Cantidades básicas
                baseQuantity: detailItem.baseQuantity || 0,
                parallelQuantity: detailItem.pararellQuantity || 0,

                // Información adicional
                total: detailItem.total || detailItem.subTotal || 0,

                // 🆕 Información de lotes
                batches: batches,
                hasBatches: batches.length > 0,
                batchCount: batches.length,

                // Unidades de medida (preserve full objects for cell renderer)
                baseUnitOfMeasure: detailItem.baseUnitOfMeasure || null,
                parallelUnitOfMeasure: detailItem.pararellUnitOfMeasure || null,

                // Referencias originales (por si necesitamos más datos después)
                originalDocument: {
                  id: document._id,
                  serial: document.serial,
                  typeCode: document.type?.code
                },
                originalDetail: {
                  id: detailItem._id
                },
                deliveryLabelsManuallySelect: detailItem.deliveryLabelsManuallySelect === true
              };

              materialsList.push(materialItem);
            }
          });
        }
      });

      // Log información sobre lotes
      const materialsWithBatches = materialsList.filter(m => m.hasBatches);
      const totalBatches = materialsList.reduce((sum, m) => sum + m.batchCount, 0);

      // Crear respuesta tipada
      const result: WorkProgressMaterialsResponse = {
        materials: materialsList,
        totalItems: materialsList.length,
        documentsProcessed: documents.length,
        materialsWithBatches: materialsWithBatches.length,
        totalBatches: totalBatches
      };

      return result;

    } catch (error) {
      console.error('Error getting work in progress materials:', error);//TO DO: Revisar con Keven
      return {
        materials: [],
        totalItems: 0,
        documentsProcessed: 0,
        materialsWithBatches: 0,
        totalBatches: 0
      };
    }
  }

  /**
   * Obtiene materiales de trabajo en curso con datos completos para análisis forense
   * Incluye información de cliente, documento, clase de documento y lotes específicos
   * @param tripId ID del viaje 
   * @param options Opciones de paginación
   */
  async getWorkInProgressMaterialsComplete(
    tripId: number,
    options: IPagination<OrderAttributes> = {
      currentPage: 1,
      limitPerPage: 1000,
      search: "",
      sort: "createdAt",
      sortDirection: "ASC"
    }
  ): Promise<WorkProgressMaterialsResponse> {
    try {
      // Usar los tipos de documento definidos para trabajo en curso
      const response = await this.findAllWithCountForDocument(
        options,
        tripId,
        DOCUMENT_TYPES_WORK_IN_PROGRESS_TRIPS_SECTION
      ).toPromise();

      // @ts-ignore - Mantener compatibilidad con tipado existente
      const documents: DocumentItem[] = response?.data?.[0]?.data || [];
      const materialsList: WorkProgressMaterialItem[] = [];

      // Procesar cada documento
      documents
        // TODO: Remove this filter when the issue is fixed or we find a different way to handle it
        .filter((d) => d.statusId != StatusEnum.INACTIVE)
        .forEach((document: any) => {
          // Verificar que el documento tenga detalles
          if (!document.detail || !Array.isArray(document.detail)) {
            return;
          }

          // Procesar cada ítem del detalle del documento
          document.detail.forEach((detailItem: any) => {
            const material = detailItem.material;

            if (!material) {
              return;
            }

            // Procesar información de lotes si existe
            const batches: BatchInfo[] = [];
            if (detailItem.batches && Array.isArray(detailItem.batches) && detailItem.batches.length > 0) {
              detailItem.batches.forEach((batch: any) => {
                batches.push({
                  batchNumber: batch.batchNumber || batch.batch || batch.bacth || batch.lote || 'N/A',
                  quantity: batch.quantity || batch.cantidad || 0,
                  expirationDate: batch.expirationDate || batch.fechaVencimiento || null,
                  manufacturingDate: batch.manufacturingDate || batch.fechaFabricacion || null,
                  status: batch.status || batch.estado || null,
                  warehouse: batch.warehouse || batch.almacen || null,
                  labels: batch?.labels || [],
                  parallelQuantity: batch?.parallelQuantity || 0
                });
              });
            }

            // Crear el item completo de material con toda la información necesaria
            const materialItem: WorkProgressMaterialItem = {
              // Información del material
              materialCode: material.code || 'N/A',
              materialName: material.name || 'Sin nombre',

              // Información del cliente (shipTo para análisis forense)
              clientCode: document.shipTo?.externalKeys?.code || document.shipTo?.code || 'N/A',
              clientName: document.shipTo?.name || 'Cliente no identificado',

              // Información del documento (crítica para análisis forense)
              documentNumber: document.serial || 'N/A',
              documentClass: document.class?.name || document.type?.name || 'Clase no identificada',

              // Cantidades detalladas
              baseQuantity: detailItem.baseQuantity || 0,
              parallelQuantity: detailItem.pararellQuantity || detailItem.parallelQuantity || 0,
              total: detailItem.total || detailItem.subTotal || 0,

              // Información completa de lotes
              batches: batches,
              hasBatches: batches.length > 0,
              batchCount: batches.length,

              // Unidades de medida (preserve full objects for cell renderer)
              baseUnitOfMeasure: detailItem.baseUnitOfMeasure || null,
              parallelUnitOfMeasure: detailItem.pararellUnitOfMeasure || null,

              // Referencias originales para trazabilidad
              originalDocument: {
                id: document._id || document.id,
                serial: document.serial,
                typeCode: document.type?.code || 'N/A'
              },
              originalDetail: {
                id: detailItem._id || detailItem.id
              },
              deliveryLabelsManuallySelect: detailItem.deliveryLabelsManuallySelect === true
            };

            materialsList.push(materialItem);
          });
        });

      // Calcular estadísticas
      const materialsWithBatches = materialsList.filter(m => m.hasBatches);
      const totalBatches = materialsList.reduce((sum, m) => sum + m.batchCount, 0);

      // Retornar respuesta completa
      const result: WorkProgressMaterialsResponse = {
        materials: materialsList,
        totalItems: materialsList.length,
        documentsProcessed: documents.length,
        materialsWithBatches: materialsWithBatches.length,
        totalBatches: totalBatches
      };

      return result;

    } catch (error) {
      return {
        materials: [],
        totalItems: 0,
        documentsProcessed: 0,
        materialsWithBatches: 0,
        totalBatches: 0
      };
    }
  }

  /**
      * Pagination
      * Servicio para traer solo las facturas para un tipo de pago
      * @param options Pagination options
      * @returns Paginated data
      */
  findAllWithCountForPaymentType(
    options: IPagination<OrderAttributes>,
    tripId: number,
    document_type: string[],
    serial: string[]
  ): Observable<IPaginationResult<OrderAttributes>> {
    const query = buildQuery<OrderAttributes>(
      where('_deleted', Ops.eq(false)),
      andAllWhere('type.code' as keyof OrderAttributes, Ops.in(...document_type)),
      andAllWhere('serial' as keyof OrderAttributes, Ops.in(...serial)),
      andAllWhere('trip.id' as keyof OrderAttributes, Ops.eq(tripId)),
      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiOrder +
      `api/${order}/withCountByTrip?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<OrderAttributes>>(API_URL);
  }

}
